import UserManagementFormComponent from "@/components/Settings/UserManagement/UserManagementForm/UserManagementForm.component";
import { EventBus } from "@/event-bus";
import { Role } from "@/interfaces/roles";
import { policyList, store } from "@/internal";
import { i18n } from "@/plugins/i18n";
import { geographicalService } from "@/services/geographical.service";
import { locationsService } from "@/services/locations.service";
import { permissionRoleService } from "@/services/permissions.service";
import { traceabilityService } from "@/services/traceability.service";
import { usersService } from "@/services/user.service";
import {
  FormField,
  FormFieldTypes,
  FormModel,
  TableFieldType,
  TableHeader
} from "helix-vue-components";

export const UserTableMetadata: TableHeader[] = [
  {
    label: String(i18n.t("image")),
    value: "avatar_thumb_url",
    fieldComponent: TableFieldType.avatar,
    class: "tdt__headers__fieldLong",
    sortable: false
  },
  {
    label: String(i18n.t("first_name")),
    value: "first_name",
    fieldComponent: TableFieldType.string,
    class: "tdt__headers__fieldLong",
    sortable: true,
    descending: false
  },
  {
    label: String(i18n.t("middle_name")),
    value: "middle_name",
    fieldComponent: TableFieldType.string,
    class: "tdt__headers__fieldLong",
    sortable: true,
    descending: false
  },
  {
    label: String(i18n.t("last_name")),
    value: "last_name",
    fieldComponent: TableFieldType.string,
    class: "tdt__headers__fieldLong",
    sortable: true,
    descending: false
  }
];

export const profileForm: FormField[] = [
  {
    component: FormFieldTypes.image,
    label: "",
    name: "avatar",
    options: {
      defaultImageField: "avatar_medium_url",
      validationRules: [""],
      placeholder: i18n.t("avatar")
    }
  },
  {
    component: FormFieldTypes.textField,
    label: i18n.t("first_name"),
    name: "first_name",
    id: "input_firstname",
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("first_name"),
      disableCondition: () => true,
      row: 1,
      flex: 3
    }
  },
  {
    component: FormFieldTypes.textField,
    label: i18n.t("middle_name"),
    name: "middle_name",
    id: "input_middlename",
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("middle_name"),
      disableCondition: () => true,
      row: 1,
      flex: 3
    }
  },
  {
    component: FormFieldTypes.textField,
    label: i18n.t("last_name"),
    name: "last_name",
    id: "input_lastname",
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("last_name"),
      disableCondition: () => true,
      row: 1,
      flex: 3
    }
  },
  {
    component: FormFieldTypes.textField,
    label: i18n.t("company_email"),
    name: "profile.company_email_address",
    id: "input_company_name",
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("company_email"),
      disableCondition: () => true,
      row: 1,
      flex: 3
    }
  },
  {
    component: FormFieldTypes.chip,
    label: i18n.t("permission_profile"),
    name: "permission_profile",
    options: {
      getData: async (ref: any) => {
        return (
          (ref.model.roles && ref.model.roles.map((role: Role) => role.name)) ||
          []
        );
      }
    }
  }
];

export const profileFormDetail: FormField[] = [
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.textField,
    label: i18n.t("display_name"),
    name: "nickname",
    id: "input_nickname",
    options: {
      validationRules: [""],
      placeholder: i18n.t("display_name"),
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.textField,
    label: i18n.t("personal_email"),
    name: "profile.personal_email",
    id: "input_personal_name",
    options: {
      validationRules: [""],
      placeholder: i18n.t("personal_email"),
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.textField,
    label: i18n.t("phone"),
    name: "profile.phone_number",
    id: "input_phone",
    options: {
      validationRules: [""],
      placeholder: i18n.t("phone"),
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.textField,
    label: i18n.t("mailing_address"),
    name: "profile.mailing_address",
    id: "input_mailing_address",
    options: {
      validationRules: [""],
      placeholder: i18n.t("mailing_address"),
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("country"),
    name: "profile.country_id",
    component: FormFieldTypes.autocomplete,
    id: "input_country",
    options: {
      row: 1,
      flex: 3,
      placeholder: i18n.t("select_country"),
      selectOptions: async () => await geographicalService.getCountries(),
      dependEmitField: "profile.state_id",
      associationText: "name",
      associationValue: "id"
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("state"),
    name: "profile.state_id",
    component: FormFieldTypes.autocomplete,
    id: "input_state",
    options: {
      dependOnField: "profile.country_id",
      placeholder: i18n.t("select_state"),
      selectOptions: async (arg: FormModel) => {
        return geographicalService.getStates(
          arg.currentModel.profile.country_id
        );
      },
      associationText: "name",
      associationValue: "id",
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.textField,
    label: i18n.t("city"),
    name: "profile.city",
    id: "input_city",
    options: {
      placeholder: i18n.t("city"),
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.textField,
    label: i18n.t("zip_code"),
    name: "profile.zip",
    id: "input_zipcode",
    options: {
      validationRules: [""],
      placeholder: i18n.t("zip_code"),
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.datepicker,
    label: i18n.t("birthdate"),
    name: "profile.birthday",
    options: {
      validationRules: [""],
      pickerOptions: {
        id: "input_birthday",
        placeholder: i18n.t("birthdate").toString(),
        "value-format": "yyyy-MM-dd"
      },
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.select,
    label: i18n.t("gender"),
    name: "profile.gender",
    id: "input_gender",
    options: {
      validationRules: [""],
      placeholder: i18n.t("gender"),
      associationText: "text",
      associationValue: "value",
      selectOptions: () => [
        { value: "male", text: i18n.t("male") },
        { value: "female", text: i18n.t("female") },
        { value: "other", text: i18n.t("other") },
        { value: "no_answer", text: i18n.t("no_answer") }
      ],
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.textField,
    label: i18n.t("emergency_contact"),
    name: "profile.emergency_contact",
    id: "input_emergency_contact",
    options: {
      validationRules: [""],
      placeholder: i18n.t("emergency_contact"),
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.textField,
    label: i18n.t("emergency_contact_number"),
    name: "profile.emergency_contact_number",
    id: "input_emergency_number",
    options: {
      validationRules: [""],
      placeholder: i18n.t("emergency_contact_number"),
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.textField,
    label: i18n.t("driver_license"),
    name: "profile.drivers_license_number",
    id: "input_driverlicense",
    options: {
      validationRules: [""],
      placeholder: i18n.t("driver_license"),
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.datepicker,
    label: i18n.t("expiration_driver_license"),
    name: "profile.drivers_license_expiration",
    options: {
      validationRules: [""],
      pickerOptions: {
        placeholder: i18n.t("expiration_driver_license").toString(),
        "value-format": "yyyy-MM-dd",
        id: "input_exp_driverlicense"
      },
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.select,
    label: i18n.t("preferred_language"),
    name: "settings.locale",
    id: "input_language",
    options: {
      row: 1,
      flex: 3,
      validationRules: ["required"],
      placeholder: i18n.t("preferred_language"),
      associationText: "text",
      associationValue: "value",
      selectOptions: () => [
        { value: "es", text: i18n.t("spanish") },
        { value: "en", text: i18n.t("english") }
      ]
    }
  },
  {
    tab: i18n.t("authorization") + "/1",
    component: FormFieldTypes.buttonEvent,
    label: i18n.t("add_pin_code"),
    name: "",
    id: "btn_addpin",
    options: {
      click: () => {
        usersService.generateNewPin();
      },
      row: 1,
      flex: 2,
      buttonClass: "mb-4 ml-0 button"
    }
  },
  {
    tab: i18n.t("authorization") + "/1",
    component: FormFieldTypes.passwordField,
    label: i18n.t("password"),
    name: "password",
    id: "input_pass",
    options: {
      validationRules: ["min:8"],
      row: 2,
      flex: 2
    }
  },
  {
    tab: i18n.t("authorization") + "/1",
    component: FormFieldTypes.passwordField,
    label: i18n.t("confirm_password"),
    name: "confirm_password",
    id: "input_confirm_pass",
    options: {
      validationRules: [""],
      dependOnField: "password",
      row: 2,
      flex: 2
    }
  },
  {
    tab: i18n.t("authorization") + "/1",
    component: FormFieldTypes.buttonEvent,
    label: i18n.t("change_password"),
    name: "change_password",
    id: "btn_change_pass",
    options: {
      click: (reference: any) => {
        if (
          !reference.errors.items.length &&
          reference.currentModel.password ===
            reference.currentModel.confirm_password
        ) {
          usersService.changePassword(reference.currentModel.password);
        }
      },
      row: 2,
      flex: 2,
      buttonClass: "mt-4 button"
    }
  }
];

export const metrcFormDetail: FormField[] = [
  {
    tab: i18n.t("metrc_info"),
    component: FormFieldTypes.textField,
    label: i18n.t("occupational_license_number"),
    name: "metrc.occupational_license_number",
    id: "input_ocu_license",
    options: {
      validationRules: [""],
      placeholder: i18n.t("occupational_license_number"),
      row: 1,
      flex: 4
    }
  },
  {
    tab: i18n.t("metrc_info"),
    component: FormFieldTypes.datepicker,
    label: i18n.t("expiration"),
    name: "metrc.expiration",
    options: {
      pickerOptions: {
        id: "input_ocu_licenseexp",
        placeholder: i18n.t("expiration").toString(),
        "value-format": "yyyy-MM-dd"
      },
      row: 1,
      flex: 3
    }
  },
  {
    tab: i18n.t("metrc_info"),
    component: FormFieldTypes.textField,
    label: i18n.t("metrc_user_key"),
    name: "metrc.user_key",
    options: {
      placeholder: i18n.t("add_user_key"),
      row: 2,
      flex: 7,
      visibleCondition: () => !!+process.env.VUE_APP_METRC
    }
  },
  {
    tab: i18n.t("metrc_info"),
    component: FormFieldTypes.buttonEvent,
    label: i18n.t("test_connection"),
    name: "",
    id: "btn_test_connection",
    options: {
      click: async args => {
        const testRes = await traceabilityService.testUserMetrcConfig(
          args.currentModel.metrc
        );
        EventBus.$emit("metrUserTest", testRes);
      },
      row: 2,
      flex: 2,
      buttonClass: "my-4 button",
      visibleCondition: () => !!+process.env.VUE_APP_METRC
    }
  }
];
export const biotrackFormDetail: FormField[] = [
  {
    tab: i18n.t("biotrack_traceability"),
    component: FormFieldTypes.textField,
    label: i18n.t("user_name"),
    name: "biotrack.username",
    id: "user_name",
    options: {
      placeholder: "",
      row: 2,
      flex: 4
    }
  },
  {
    tab: i18n.t("biotrack_traceability"),
    component: FormFieldTypes.passwordField,
    label: i18n.t("password"),
    name: "biotrack.password",
    id: "input_pass",
    options: {
      placeholder: "",
      row: 2,
      flex: 3
    }
  },
  {
    tab: i18n.t("biotrack_traceability"),
    component: FormFieldTypes.buttonEvent,
    label: i18n.t("test_connection"),
    name: "",
    id: "btn_test_connection",
    options: {
      click: async args => {
        const testRes = await traceabilityService.testUserBiotrackConfig(
          args.currentModel.biotrack
        );
        EventBus.$emit("biotrackUserTest", testRes);
      },
      disableCondition: () => {
        return UserManagementFormComponent.currentBiotrackStatus;
      },
      row: 2,
      flex: 2,
      buttonClass: "my-4 button"
    }
  }
];

export const userFormMetadata: FormField[] = [
  {
    tab: i18n.t("general_info") + "/0",
    label: "",
    name: "avatar",
    component: FormFieldTypes.image,
    options: {
      defaultImageField: "avatar_medium_url",
      row: 1,
      flex: 12
    }
  }
];

export const userFormDetail: FormField[] = [
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("first_name"),
    name: "first_name",
    id: "input_first_name",
    component: FormFieldTypes.textField,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("first_name"),
      row: 2,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("middle_name"),
    name: "middle_name",
    id: "input_middle_name",
    component: FormFieldTypes.textField,
    options: {
      placeholder: i18n.t("middle_name"),
      row: 2,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("last_name"),
    name: "last_name",
    id: "input_last_name",
    component: FormFieldTypes.textField,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("last_name"),
      row: 2,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("birthdate"),
    name: "profile.birthday",
    component: FormFieldTypes.datepicker,
    id: "datepicker",
    options: {
      validationRules: ["required"],
      pickerOptions: {
        id: "date_birthdate_input",
        placeholder: i18n.t("birthday").toString(),
        required: true,
        "value-format": "yyyy-MM-dd"
      },
      row: 2,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("mailing_address"),
    name: "profile.mailing_address",
    id: "input_address",
    component: FormFieldTypes.textField,
    options: {
      placeholder: i18n.t("mailing_address"),
      row: 3,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("country"),
    name: "profile.country_id",
    id: "input_country",
    component: FormFieldTypes.autocomplete,
    options: {
      row: 3,
      flex: 3,
      placeholder: i18n.t("select_country"),
      selectOptions: async () => await geographicalService.getCountries(),
      dependEmitField: "profile.state_id",
      associationText: "name",
      associationValue: "id",
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("state"),
    name: "profile.state_id",
    id: "input_state",
    component: FormFieldTypes.autocomplete,
    options: {
      dependOnField: "profile.country_id",
      placeholder: i18n.t("select_state"),
      selectOptions: async (arg: FormModel) => {
        return geographicalService.getStates(
          arg.currentModel.profile.country_id
        );
      },
      associationText: "name",
      associationValue: "id",
      row: 3,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("city"),
    name: "profile.city",
    id: "input_city",
    component: FormFieldTypes.textField,
    options: {
      row: 3,
      flex: 3,
      placeholder: i18n.t("city"),
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("zip_code"),
    name: "profile.zip",
    id: "input_zip",
    component: FormFieldTypes.textField,
    options: {
      placeholder: i18n.t("zip_code"),
      row: 4,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("company_email"),
    name: "profile.company_email_address",
    id: "input_company_email",
    component: FormFieldTypes.textField,
    options: {
      validationRules: ["required", "email"],
      placeholder: i18n.t("company_email"),
      row: 4,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("personal_email"),
    name: "profile.personal_email",
    id: "input_personal_email",
    component: FormFieldTypes.textField,
    options: {
      validationRules: ["email"],
      placeholder: i18n.t("personal_email"),
      row: 4,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("phone"),
    name: "profile.phone_number",
    id: "input_phone",
    component: FormFieldTypes.textField,
    options: {
      row: 4,
      flex: 3,
      placeholder: i18n.t("phone"),
      vuetifyProps: {
        mask: "phone"
      },
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("driver_license"),
    name: "profile.drivers_license_number",
    id: "input_driver_license",
    component: FormFieldTypes.textField,
    options: {
      row: 5,
      flex: 3,
      placeholder: i18n.t("driver_license"),
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("expiration_driver_license"),
    name: "profile.drivers_license_expiration",
    component: FormFieldTypes.datepicker,
    id: "datepicker",
    options: {
      row: 5,
      flex: 3,
      pickerOptions: {
        id: "date_exdriverli_input",
        placeholder: i18n.t("expiration_driver_license").toString(),
        "value-format": "yyyy-MM-dd"
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("occupational_license_number"),
    name: "profile.occupational_license_number",
    id: "input_ocupli",
    component: FormFieldTypes.textField,
    options: {
      placeholder: i18n.t("occupational_license_number"),
      row: 5,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("occupational_license_expiration"),
    name: "profile.occupational_license_expiration",
    component: FormFieldTypes.datepicker,
    id: "datepicker",
    options: {
      pickerOptions: {
        id: "date_oexplic_input",
        placeholder: i18n
          .t("occupational_license_number_expiration")
          .toString(),
        "value-format": "yyyy-MM-dd"
      },
      row: 5,
      flex: 3
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("gender"),
    name: "profile.gender",
    id: "cbx_gender",
    component: FormFieldTypes.enum,
    options: {
      placeholder: i18n.t("gender"),
      enumSelectOptions: [
        { value: "male", text: i18n.t("male") },
        { value: "female", text: i18n.t("female") },
        { value: "other", text: i18n.t("other") },
        { value: "no_answer", text: i18n.t("no_answer") }
      ],
      row: 6,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("hourly_rate"),
    name: "profile.hourly_rate",
    id: "input_hourly",
    component: FormFieldTypes.numericField,
    options: {
      placeholder: i18n.t("hourly_rate"),
      row: 6,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    label: i18n.t("location.location"),
    name: "settings.current_location_id",
    id: "cbx_location",
    component: FormFieldTypes.select,
    options: {
      validationRules: ["required"],
      placeholder: i18n.t("location_placeholder"),
      selectOptions: async () => await locationsService.getLocations(),
      associationText: "name",
      associationValue: "id",
      row: 6,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.switch,
    label: i18n.t("pharmacist").toString(),
    name: "pharmacist",
    options: {
      row: 6,
      flex: 3,
      vuetifyProps: {
        trueValue: "1",
        falseValue: "0"
      },
      disableCondition: () =>
        !store.getters["UserModule/pharmacistInfoFetched"] &&
        store.getters["UserModule/disabledMode"]
    }
  },
  {
    tab: i18n.t("general_info") + "/0",
    component: FormFieldTypes.autocomplete,
    label: i18n.t("permission_profile"),
    name: "roles",
    id: "cbx_role",
    defaultValue: [],
    options: {
      placeholder: i18n.t("type_to_search"),
      validationRules: ["required"],
      selectOptions: async () => {
        const roles = await permissionRoleService.getRoles();
        return roles;
      },
      associationText: "name",
      associationValue: "id",
      row: 7,
      flex: 3,
      vuetifyProps: {
        "no-data-text": i18n.t("no_search_results"),
        "item-value": "id",
        "deletable-chips": true,
        "cache-items": true,
        multiple: true,
        chips: true
      },
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  }
];

export const pharmacistForm: FormField[] = [
  {
    tab: i18n.t("pharmacist_info"),
    component: FormFieldTypes.textField,
    label: i18n.t("title"),
    name: "pharmacistData.pharmacist_title",
    options: {
      placeholder: i18n.t("title"),
      row: 1,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("pharmacist_info"),
    component: FormFieldTypes.textField,
    label: i18n.t("license_number"),
    name: "pharmacistData.pharmacist_license_number",
    options: {
      placeholder: i18n.t("license_number"),
      row: 1,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("pharmacist_info"),
    component: FormFieldTypes.textField,
    label: i18n.t("national_provider_identifier"),
    name: "pharmacistData.pharmacist_national_provider_identifier",
    options: {
      placeholder: i18n.t("national_provider_identifier"),
      row: 1,
      flex: 3,
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  },
  {
    tab: i18n.t("pharmacist_info"),
    component: FormFieldTypes.autocomplete,
    label: i18n.t("locations_field"),
    name: "pharmacistData.pharmacist_location.add",
    id: "pharmacist_locations",
    defaultValue: [],
    options: {
      placeholder: i18n.t("location_placeholder"),
      validationRules: [""],
      selectOptions: async () => {
        const locations = await locationsService.getLocations();
        return locations;
      },
      associationText: "name",
      associationValue: "id",
      row: 1,
      flex: 3,
      vuetifyProps: {
        "no-data-text": i18n.t("no_search_results"),
        "item-value": "id",
        "deletable-chips": true,
        "cache-items": true,
        multiple: true,
        chips: true
      },
      disableCondition: () => {
        const disable = store.getters["UserModule/disabledMode"];
        return disable;
      }
    }
  }
];
